import { useEffect, useRef, useState } from 'react';
import type { LobbyInfo } from '../../api/types';
import { useLobbyChat, useSendLobbyMessage } from '../../queries';
import { Button, Dialog, LoadingBlock, StateBlock, TextInput } from '../../ui';
import { useToast } from '../../ui/toast';

function formatTime(unixSeconds: number): string {
  if (!unixSeconds) return '';
  return new Date(unixSeconds * 1000).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function LobbyChatDialog({
  lobby,
  onClose,
}: {
  lobby: LobbyInfo | null;
  onClose: () => void;
}) {
  const open = lobby !== null;
  const [draft, setDraft] = useState('');
  const toast = useToast();
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const { data, isPending, isError, error } = useLobbyChat(lobby?.bot_id, open);
  const sendMessage = useSendLobbyMessage();

  const messages = data?.messages ?? [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  useEffect(() => {
    if (!open) setDraft('');
  }, [open]);

  const send = () => {
    const message = draft.trim();
    if (!message || !lobby) return;
    sendMessage.mutate(
      { botId: lobby.bot_id, message },
      {
        onSuccess: () => setDraft(''),
        onError: (mutationError) => toast.error('Message not sent', (mutationError as Error).message),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Lobby chat"
      description={lobby?.game_name}
      footer={
        <div className="flex w-full gap-2">
          <TextInput
            value={draft}
            placeholder="Message the lobby…"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send();
              }
            }}
          />
          <Button variant="primary" loading={sendMessage.isPending} onClick={send}>
            Send
          </Button>
        </div>
      }
    >
      {isPending ? (
        <LoadingBlock label="Loading chat…" />
      ) : isError ? (
        <StateBlock tone="bad" title="Chat unavailable" detail={(error as Error).message} />
      ) : messages.length === 0 ? (
        <StateBlock title="No messages yet" detail="Anything said in the lobby shows up here." />
      ) : (
        <ul className="space-y-2">
          {messages.map((message, index) => (
            <li
              key={`${message.timestamp_unix}-${index}`}
              className="rounded-md border border-line bg-surface-2/60 px-3 py-2"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs font-semibold text-gold-300">
                  {message.persona_name || `Account ${message.account_id}`}
                </span>
                <span className="text-[11px] text-ink-faint">
                  {formatTime(message.timestamp_unix)}
                </span>
              </div>
              <p className="mt-1 text-sm break-words text-ink">{message.text}</p>
            </li>
          ))}
          <div ref={bottomRef} />
        </ul>
      )}
    </Dialog>
  );
}
